// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, h, Prop, readTask, State, Watch } from '@stencil/core'

import { isOverflowing } from '../../helpers'
import type { Size } from '../shared'

/**
 * `<qds-label>` elements represent a caption for an item in a user interface.
 *
 * @see https://quartz.se.com/build/components/label
 */
@Component({
  tag: 'qds-label',
  shadow: true,
  styleUrl: 'label.css',
})
export class Label implements ComponentInterface {
  /**
   * Adds vertical margin to the label for alignment.
   *
   * This is useful when creating inline layouts so that the first lines have
   * the correct vertical centering.
   */
  @Prop() public readonly inline: boolean = false

  /**
   * Specify the labelled item as required by appending a red asterisk (*).
   */
  @Prop() public readonly required: boolean = false

  /**
   * The label's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The label's text.
   */
  @Prop() public readonly text?: string

  @State() private tooltip = false

  #label?: HTMLSpanElement

  #ro?: ResizeObserver

  #span?: HTMLSpanElement

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  @Watch('text')
  protected textChanged(): void {
    if (this.#ro) this.#ro.disconnect()

    if (!this.#span) return

    const span = this.#span
    readTask(() => {
      this.tooltip = isOverflowing(span)
    })
    this.#ro = new ResizeObserver(([spanEntry]) => {
      this.tooltip = isOverflowing(spanEntry.target)
    })
    this.#ro.observe(this.#span)
  }

  public componentDidLoad(): void {
    this.textChanged()
  }

  public disconnectedCallback(): void {
    if (this.#ro) this.#ro.disconnect()
  }

  public render() {
    return (
      <span
        class={{
          'qds-inline': this.inline,
          'qds-label': true,
        }}
        data-size={this.#computedSize}
        ref={this.#labelRef}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={this.tooltip ? 0 : undefined}
      >
        {this.required && <span class="qds-required">*</span>}
        <span class="qds-text" ref={this.#spanRef}>
          {this.text}
        </span>
        {this.tooltip && (
          <qds-tooltip aria-hidden="true" ref={this.#tooltipRef}>
            {this.text}
          </qds-tooltip>
        )}
      </span>
    )
  }

  readonly #labelRef = (label?: HTMLSpanElement): void => {
    this.#label = label
  }

  readonly #spanRef = (span?: HTMLSpanElement): void => {
    this.#span = span
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#label
  }
}
